"use client";

import type { OrbTextureName } from "@ui/components/core/Orb/Orb";
import {
  ChatPlayer,
  type ChatPlayerPlayback,
  type ChatPlayerProps,
  type ChatPlayerSource,
  type ChatPlayerTranscriptSource,
} from "@ui/components/core/ChatPlayer";
import { Orb } from "@ui/components/core/Orb/Orb";
import IconMuteFill from "@ui/central-icons/icons/IconMuteFill";
import IconPlayFill from "@ui/central-icons/icons/IconPlayFill";
import IconVolumeFullFill from "@ui/central-icons/icons/IconVolumeFullFill";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { useAudioElementOrbAnalyser } from "@ui/hooks/useNativeAudioOrbAnalyser";
import { getAudioUrl } from "@utilities/payload";
import { clsx } from "clsx";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createChatPlayerSourceFromChatTranscript, type AudioDemoPreviewSpeakerRole } from "./transcript";
import { type PreviewAudioController, usePreviewAudioController } from "./usePreviewAudioController";

type AudioPreviewPlaybackState = "idle" | "playing" | "manualStartRequired";
type AudioPreviewControlMode = "mute" | "play" | "unmute";

export type AudioDemoPreviewProps = {
  audio?: Parameters<typeof getAudioUrl>[0] | null;
  chatTranscript?: ChatPlayerTranscriptSource | null;
  className?: string;
  orbTexture?: OrbTextureName | null;
  previewAudio?: PreviewAudioController;
  speakerRoles?: AudioDemoPreviewSpeakerRole[] | null;
};

const AUDIO_ASSISTANT_MESSAGE_VARIANTS = {
  variant: "frostedGray",
  maxWidth: "compact",
  tail: "none",
} satisfies ChatPlayerProps["assistantMessageVariants"];
const AUDIO_USER_MESSAGE_VARIANTS = {
  variant: "elevated",
  tail: "left",
} satisfies ChatPlayerProps["userMessageVariants"];

function getAudioPreviewControlMode(playbackState: AudioPreviewPlaybackState, isMuted: boolean) {
  switch (playbackState) {
    case "manualStartRequired":
      return "play";
    case "idle":
    case "playing":
      return isMuted ? "unmute" : "mute";
  }
}

function DemoChatPlayer({ playback, source }: { playback: ChatPlayerPlayback; source: ChatPlayerSource }) {
  return (
    <div className='tw-relative tw-h-[90%] md:tw-h-80 tw-w-full'>
      <ChatPlayer
        source={source}
        playback={playback}
        assistantMessageVariants={AUDIO_ASSISTANT_MESSAGE_VARIANTS}
        userMessageVariants={AUDIO_USER_MESSAGE_VARIANTS}
        className='tw-size-full'
        anchor='bottom'
      />
      <div
        aria-hidden='true'
        className='tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-0 tw-z-10 tw-h-18 tw-bg-gradient-to-b tw-from-cream tw-from-30% tw-to-transparent'
      />
    </div>
  );
}

function syncNativeAudioMuteState(audioElement: HTMLAudioElement, isMuted: boolean) {
  // Apply native mute before autoplay starts; Safari can otherwise leak a tiny first audio frame.
  audioElement.defaultMuted = isMuted;
  audioElement.muted = isMuted;

  try {
    audioElement.volume = isMuted ? 0 : 1;
  } catch {
    // Some Safari versions ignore or reject programmatic volume changes.
  }
}

function AudioDemoPreviewContent({
  audio,
  chatTranscript,
  className,
  orbTexture = "agents-3",
  previewAudio,
  speakerRoles,
}: AudioDemoPreviewProps & { previewAudio: PreviewAudioController }) {
  const t = useTranslations("ClientKeys.Globals");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { isActiveMediaElement, isMuted, setActiveMediaElement, setMuted } = previewAudio;
  const isMutedRef = useRef(isMuted);
  const [mediaElement, setMediaElement] = useState<HTMLAudioElement | null>(null);
  const [playbackState, setPlaybackState] = useState<AudioPreviewPlaybackState>("idle");
  const { ensureAudioContext, outputAnalyser } = useAudioElementOrbAnalyser(audioRef, "AudioDemoPreview", {
    outputMuted: isMuted,
  });
  const audioUrl = audio ? getAudioUrl(audio) : undefined;
  const isPreviewPlaying = playbackState === "playing";
  const controlMode = getAudioPreviewControlMode(playbackState, isMuted);
  const actionLabel = t(controlMode);
  const chatSource = useMemo(
    () => createChatPlayerSourceFromChatTranscript(chatTranscript, speakerRoles),
    [chatTranscript, speakerRoles],
  );

  const setAudioElement = useCallback(
    (audioElement: HTMLAudioElement | null) => {
      const previousAudioElement = audioRef.current;

      if (audioElement) {
        syncNativeAudioMuteState(audioElement, isMutedRef.current);
      }

      audioRef.current = audioElement;
      setMediaElement(audioElement);
      setActiveMediaElement(audioElement, previousAudioElement);
    },
    [setActiveMediaElement],
  );

  useEffect(() => {
    isMutedRef.current = isMuted;

    if (!mediaElement) return;

    syncNativeAudioMuteState(mediaElement, isMuted);
  }, [isMuted, mediaElement]);

  useEffect(() => {
    if (!mediaElement || !audioUrl) return;

    let isActive = true;

    mediaElement.currentTime = 0;
    setPlaybackState("idle");
    void mediaElement.play().catch(() => {
      if (!isActive) return;

      setPlaybackState("manualStartRequired");
    });

    return () => {
      isActive = false;
      mediaElement.pause();
      mediaElement.currentTime = 0;
      setPlaybackState("idle");
    };
  }, [audioUrl, mediaElement]);

  const handlePreviewControl = () => {
    switch (controlMode) {
      case "play":
        if (!mediaElement) return;

        setMuted(false);
        ensureAudioContext();
        setPlaybackState("idle");
        void mediaElement.play().catch(() => {
          if (!isActiveMediaElement(mediaElement)) return;

          setMuted(isMuted);
          setPlaybackState("manualStartRequired");
        });
        return;

      case "mute":
      case "unmute": {
        const nextIsMuted = controlMode === "mute";

        setMuted(nextIsMuted);

        if (!nextIsMuted) {
          ensureAudioContext();
        }
      }
    }
  };

  return (
    <>
      <div
        className={clsx(
          "tw-relative tw-h-full tw-min-h-0 tw-w-full tw-max-w-96 sm:tw-max-w-xl md:tw-max-w-2xl tw-pt-5",
          className,
        )}
      >
        <div
          className={clsx(
            "tw-absolute tw-left-1/2 tw-top-1/2 tw-size-56 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-transition-transform tw-duration-500 sm:tw-size-64 md:tw-size-72",
            isPreviewPlaying && "tw-scale-[1.03]",
          )}
        >
          <div className='tw-absolute tw-inset-0 tw-rounded-full tw-overflow-hidden'>
            <Orb
              texture={orbTexture ?? "agents-3"}
              animated={audioUrl ? isPreviewPlaying : true}
              outputAnalyser={isPreviewPlaying ? outputAnalyser : null}
            />
          </div>
        </div>

        {audioUrl && chatSource && <DemoChatPlayer source={chatSource} playback={{ type: "media", mediaElement }} />}

        {audioUrl && (
          <audio
            ref={setAudioElement}
            src={audioUrl}
            muted={isMuted}
            loop
            autoPlay
            preload='auto'
            className='tw-hidden'
            aria-hidden='true'
            crossOrigin='anonymous'
            onPlay={() => setPlaybackState("playing")}
            onPause={() => setPlaybackState("idle")}
          />
        )}
      </div>

      {audioUrl && (
        <div className='tw-pointer-events-none tw-absolute tw-inset-0 tw-z-20 tw-flex tw-items-start tw-justify-end sm:tw-items-center sm:tw-justify-center tw-mt-5 sm:tw-mt-0'>
          <button
            type='button'
            onClick={handlePreviewControl}
            aria-label={actionLabel}
            className={clsx(
              "tw-group/preview-button tw-pointer-events-auto tw-relative tw-flex tw-size-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-black/20 tw-text-white tw-shadow-sm tw-backdrop-blur-md tw-outline-none tw-transition tw-duration-200 hover:tw-scale-105 focus-visible:tw-outline-focus",
              controlMode === "mute" && "tw-scale-105",
            )}
          >
            {controlMode === "play" ? (
              <IconPlayFill aria-hidden='true' className='tw-size-6 tw-translate-x-px' />
            ) : controlMode === "unmute" ? (
              <IconMuteFill aria-hidden='true' className={clsx(iconStrokeFix, "tw-size-6")} />
            ) : (
              <IconVolumeFullFill aria-hidden='true' className={clsx(iconStrokeFix, "tw-size-6")} />
            )}
          </button>
        </div>
      )}
    </>
  );
}

export function AudioDemoPreview(props: AudioDemoPreviewProps) {
  const localPreviewAudio = usePreviewAudioController();

  return <AudioDemoPreviewContent {...props} previewAudio={props.previewAudio ?? localPreviewAudio} />;
}

export { createChatPlayerSourceFromChatTranscript } from "./transcript";
export type { AudioDemoPreviewSpeakerRole } from "./transcript";
export { usePreviewAudioController, type PreviewAudioController } from "./usePreviewAudioController";
